import React from "react";
import { formatINR, formatShortDate, daysLabel, categoryMeta, ratioColor, STATUS_META } from "../../lib/format";
import { Card } from "../ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Info, ArrowUpRight, AlertCircle } from "lucide-react";
import { InfoTip, GLOSSARY } from "../app/InfoTip";
import ShareSnapshot from "../app/ShareSnapshot";

export function RatioArc({ ratio, size = 160 }) {
  const r = size / 2 - 12;
  const c = 2 * Math.PI * r;
  const pct = Math.min(ratio, 100) / 100;
  const color = ratioColor(ratio);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F5F9" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - c * pct}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-4xl font-light text-ink tnum">{ratio}%</span>
        <span className="text-xs text-muted-foreground mt-0.5">committed</span>
      </div>
    </div>
  );
}

export function CommitmentRow({ c, onClick }) {
  const meta = categoryMeta(c.category);
  const Icon = meta.icon;
  const st = STATUS_META[c.status] || STATUS_META.active;
  return (
    <button
      onClick={onClick}
      data-testid={`commitment-row-${c.id}`}
      className="w-full flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-secondary transition-colors text-left"
    >
      <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center shrink-0">
        <Icon size={18} strokeWidth={1.5} className="text-navy" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-ink truncate">{c.name}</p>
          {c.detection_source && c.detection_source !== "manual" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-mint-light text-mint shrink-0">AI</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {meta.label}{c.payment_source ? ` · ${c.payment_source}` : ""}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-semibold text-ink tnum">{formatINR(c.amount)}</p>
        <p className="text-xs text-muted-foreground">
          {c.next_due_date ? `${formatShortDate(c.next_due_date)} · ${daysLabel(c.days_remaining)}` : (st.label)}
        </p>
      </div>
    </button>
  );
}

function StatTile({ label, value, sub, accent, info }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-4">
      <p className="text-xs text-muted-foreground flex items-center gap-1">{label}{info && <InfoTip {...info} className="h-4 w-4" />}</p>
      <p className={`font-heading text-xl font-medium mt-1 tnum ${accent ? "text-mint" : "text-ink"}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export default function DashboardView({ data, commitments = [], onSelect, demo = false }) {
  const p = data.position;
  const upcoming = data.upcoming || [];
  const attention = data.needs_attention || [];
  const bd = p.breakdown || {};
  const breakdownItems = [
    { k: "Essential", v: bd.essential, color: "#0f2a43" },
    { k: "Debt", v: bd.debt, color: "#1A3B5C" },
    { k: "Flexible", v: bd.flexible, color: "#10B981" },
    { k: "Shared", v: bd.shared, color: "#94A3B8" },
  ].filter((x) => x.v > 0);
  const totalBd = breakdownItems.reduce((s, x) => s + x.v, 0) || 1;

  return (
    <div className="space-y-5" data-testid="dashboard-view">
      {/* Hero card */}
      <Card className="border-none rounded-2xl bg-navy text-white shadow-card-lg overflow-hidden relative">
        <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-mint/10" />
        <div className="p-6 md:p-8 relative">
          <p className="text-sm text-white/70">Committed This Month</p>
          <p className="font-heading text-5xl md:text-6xl font-light mt-2 tnum" data-testid="committed-this-month">
            {formatINR(p.committed_this_month)}
          </p>
          <p className="text-sm text-white/60 mt-2">Already committed before discretionary spending.</p>
          <div className="flex flex-wrap gap-2 mt-5">
            <span className="text-xs px-3 py-1.5 rounded-full bg-white/10">Monthly income {formatINR(p.monthly_income)}</span>
            <span className="text-xs px-3 py-1.5 rounded-full bg-white/10">{p.active_count} active commitments</span>
          </div>
        </div>
      </Card>

      {/* Ratio + Safe to spend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="rounded-2xl border-border shadow-card-sm p-6 flex flex-col items-center justify-center" data-testid="ratio-card">
          <div className="flex items-center gap-1.5 mb-3 self-start">
            <p className="text-sm font-medium text-ink">Commitment Ratio</p>
            <InfoTip title="Commitment Ratio" testid="ratio-info"
              text="The share of your monthly income already promised to recurring commitments (annual plans spread across 12 months; one-time dues excluded). Lower is healthier." />
          </div>
          <RatioArc ratio={p.commitment_ratio} />
          <div className="mt-3 text-center">
            <span className="inline-block text-sm px-3 py-1 rounded-full bg-secondary text-slatey">{p.ratio_status}</span>
            <p className="text-xs text-muted-foreground mt-2">
              {formatINR(p.monthly_recurring)} recurring (monthly equivalent) ÷ {formatINR(p.monthly_income)} income
            </p>
          </div>
        </Card>

        <Card className="rounded-2xl border-border shadow-card-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-ink">{p.safe_label}</p>
              <InfoTip title="Safe to Spend" testid="safe-to-spend-info"
                text={p.safe_mode === "balance_based"
                  ? "The estimated amount available after your upcoming commitments and protected reserve: available balance + income before your next salary − commitments due − protected reserve."
                  : "The estimated amount available after accounting for your upcoming commitments and protected reserve: monthly income − commitments due this cycle − protected reserve."} />
            </div>
            {!demo && <ShareSnapshot position={p} />}
          </div>
          <p className="font-heading text-4xl font-light text-mint mt-2 tnum" data-testid="safe-to-spend">
            {formatINR(p.safe_to_spend)}
          </p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <StatTile label="Protected reserve" value={formatINR(p.protected_reserve)} info={GLOSSARY.protected_reserve} />
            <StatTile label="Due this cycle" value={formatINR(p.due_in_cycle)} info={GLOSSARY.due_this_cycle} />
          </div>
          {p.safe_inputs && (
            <div className="mt-4 rounded-xl bg-secondary/50 p-4" data-testid="safe-breakdown">
              <p className="text-xs font-medium text-ink mb-2">How this is calculated</p>
              <div className="space-y-1.5">
                {p.safe_inputs.map((row, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-slatey flex items-center gap-1.5">
                      <span className="w-3 text-center text-muted-foreground">{row.sign}</span>
                      {row.label}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${row.source === "user" ? "bg-mint-light text-mint" : "bg-calm-pendingbg text-calm-pending"}`}>
                        {row.source === "user" ? "you entered" : "predicted"}
                      </span>
                    </span>
                    <span className="tnum text-ink">{formatINR(row.amount)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs pt-1.5 border-t border-border/60 font-medium">
                  <span className="text-ink">{p.safe_label}</span>
                  <span className="tnum text-mint">{formatINR(p.safe_to_spend)}</span>
                </div>
              </div>
              {p.last_updated && (
                <p className="text-[10px] text-muted-foreground mt-2">Data last updated {formatShortDate(p.last_updated)}</p>
              )}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
            This is an estimate based on the information you provide and confirm. It is not a live bank balance or financial advice.
          </p>
        </Card>
      </div>

      {/* Needs attention */}
      {attention.length > 0 && (
        <Card className="rounded-2xl border-border shadow-card-sm p-6" data-testid="needs-attention">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle size={18} className="text-calm-pending" strokeWidth={1.5} />
            <p className="text-sm font-medium text-ink">Needs Attention</p>
          </div>
          <div className="space-y-2">
            {attention.map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-calm-pendingbg px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink text-sm truncate">{a.commitment?.name}</p>
                  <p className="text-xs text-slatey">{a.reason}</p>
                </div>
                <button onClick={() => onSelect && onSelect(a.commitment)} className="text-mint shrink-0" data-testid="attention-review">
                  <ArrowUpRight size={18} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Upcoming */}
      <Card className="rounded-2xl border-border shadow-card-sm p-6" data-testid="upcoming-commitments">
        <p className="text-sm font-medium text-ink mb-2">Upcoming Commitments</p>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No upcoming commitments yet.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {upcoming.map((c) => (
              <CommitmentRow key={c.id} c={c} onClick={() => onSelect && onSelect(c)} />
            ))}
          </div>
        )}
      </Card>

      {/* Breakdown */}
      {breakdownItems.length > 0 && (
        <Card className="rounded-2xl border-border shadow-card-sm p-6" data-testid="commitment-breakdown">
          <p className="text-sm font-medium text-ink mb-4">Commitment Breakdown <span className="text-xs text-muted-foreground font-normal">(monthly equivalent)</span></p>
          <div className="flex h-3 rounded-full overflow-hidden mb-4">
            {breakdownItems.map((x) => (
              <div key={x.k} style={{ width: `${(x.v / totalBd) * 100}%`, background: x.color }} />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {breakdownItems.map((x) => (
              <div key={x.k} className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: x.color }} />
                <span className="text-sm text-slatey flex-1">{x.k}</span>
                <span className="text-sm font-medium text-ink tnum">{formatINR(x.v)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
